/**
 * What a person types into a money box, read as the API takes it (F-100, A-#556): "1,000", "$1,000.50" and "1000" all
 * send "1000" or "1000.50". Commas only between groups of three, never guessed ("1,50" is not 150). Never through a
 * float: text in, text out, as money.ts. The API's rule is unchanged (a body holds plain digits only); the screen never
 * sends a "$" or a comma.
 */

export type MoneyWhy = 'not_money' | 'too_many_digits' | 'cents' | 'commas' | 'negative';
export type MoneyParse = { kind: 'empty' } | { kind: 'ok'; text: string } | { kind: 'bad'; why: MoneyWhy };
export type MoneyRules = { maxDigits?: number; negative?: boolean };

// an optional sign and "$" in either order, the dollars (commas allowed here, checked below), the cents
const TYPED = /^(?:(-)\$?|\$(-)?)?(\d[\d,]*)?(?:\.(\d*))?$/;
const GROUPED = /^\d{1,3}(,\d{3})+$/;

export function parseMoneyInput(typed: string, { maxDigits = 12, negative = false }: MoneyRules = {}): MoneyParse {
  const s = typed.trim();
  if (s === '') return { kind: 'empty' };
  const m = TYPED.exec(s);
  if (!m || (m[3] === undefined && !m[4])) return { kind: 'bad', why: 'not_money' };
  const whole = m[3] ?? '0';
  if (whole.includes(',') && !GROUPED.test(whole)) return { kind: 'bad', why: 'commas' };
  if (m[4] !== undefined && m[4] === '') return { kind: 'bad', why: 'not_money' };
  if ((m[4] ?? '').length > 2) return { kind: 'bad', why: 'cents' };
  const digits = whole.replace(/,/g, '').replace(/^0+(?=\d)/, '');
  if (digits.length > maxDigits) return { kind: 'bad', why: 'too_many_digits' };
  const minus = Boolean(m[1] ?? m[2]);
  if (minus && !negative) return { kind: 'bad', why: 'negative' };
  return { kind: 'ok', text: `${minus ? '-' : ''}${digits}${m[4] !== undefined ? `.${m[4]}` : ''}` };
}

/** The plain number the form sends, or null when the box holds nothing or something that is not money. */
export function moneyText(typed: string, rules: MoneyRules = {}): string | null {
  const p = parseMoneyInput(typed, rules);
  return p.kind === 'ok' ? p.text : null;
}

/** The same number with commas between thousands, the cents as typed: "1234567.89" reads "1,234,567.89". No "$". */
export function formatMoneyInput(text: string): string {
  const m = /^(-)?(\d+)(\.\d*)?$/.exec(text);
  if (!m) return text;
  return `${m[1] ?? ''}${m[2]!.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${m[3] ?? ''}`;
}

/** One source for every money box's help text. */
export function moneyWords(maxDigits = 12): string {
  return `Dollars and cents, up to ${maxDigits} digits, as ${maxDigits > 8 ? '1,200.50' : '85.50'}.`;
}
